import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Trash2, Plus, X, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { format } from 'date-fns';
import { useToast } from './ui/use-toast';
import { getBaseUrl } from '@/lib/api';

interface SerialNumber {
  id: string;
  partNo: string;
  spIncGST: number;
  costPrice: number;
  batchNo: string;
  mfgDate: string;
  expiryDays: string;
  auto: boolean;
  forever: boolean;
  status?: number; // Add status field
  selected?: boolean; // Add selected field for checkbox
}

interface SerialNumberEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToList: (serialNumbers: SerialNumber[]) => void;
  productName: string;
  barcode: string;
  productCode?: number;
  token?: string;
  productRate?: number;
  isDebitNote?: boolean;
  /** Debit note row location ID; passed to GetSerail when loading existing serials */
  locationId?: number;
  /** When true, hides the Cost Price field in the Add form and in the serial list */
  hideCostPrice?: boolean;
  /** Default for "Expiry forever" checkbox. From setting "Expiry forever": Value "1" = true, "0" = false */
  defaultExpiryForever?: boolean;
}

const SerialNumberEntryModal: React.FC<SerialNumberEntryModalProps> = ({
  isOpen,
  onClose,
  onAddToList,
  productName,
  barcode,
  productCode,
  token,
  productRate,
  isDebitNote = false,
  locationId,
  hideCostPrice = false,
  defaultExpiryForever = true,
}) => {
  const { toast } = useToast();
  const [serialNumbers, setSerialNumbers] = useState<SerialNumber[]>([]);
  const [currentSerial, setCurrentSerial] = useState<SerialNumber>({
    id: '',
    partNo: '',
    spIncGST: productRate || 0,
    costPrice: 0,
    batchNo: '0',
    mfgDate: format(new Date(), 'yyyy-MM-dd'),
    expiryDays: defaultExpiryForever ? '999999' : '0',
    auto: false,
    forever: defaultExpiryForever,
  });

  // Initialize spIncGST with productRate when component mounts or productRate changes
  React.useEffect(() => {
    if (productRate !== undefined && productRate !== null) {
      setCurrentSerial(prev => ({ ...prev, spIncGST: productRate }));
    }
  }, [productRate]);

  const [generateCount, setGenerateCount] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isAddingSerial, setIsAddingSerial] = useState(false);
  const [serialNumberError, setSerialNumberError] = useState('');
  const [existingSerialNumbers, setExistingSerialNumbers] = useState<SerialNumber[]>([]);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<Set<string>>(new Set());
  // Pagination for debit note existing serials (same as inventory Add Serial modal)
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const prevOpenRef = useRef(false);
  // Mobile: lazy load (no pagination), track which page we've loaded up to
  const [isMobile, setIsMobile] = useState(false);
  const [mobilePage, setMobilePage] = useState(0);
  const cardsScrollRef = useRef<HTMLDivElement>(null);
  /** When true, we've received a short batch (end of list) - don't request more pages */
  const mobileHasNoMoreRef = useRef(false);
  const MOBILE_PAGE_SIZE = 20;

  // Reset to first page when modal opens
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setCurrentPage(0);
      setMobilePage(0);
      mobileHasNoMoreRef.current = false;
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // Reset to first page when search query changes (so API search applies from page 0)
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(0);
      setMobilePage(0);
      mobileHasNoMoreRef.current = false;
    }
  }, [searchQuery]);

  // Detect mobile viewport (Tailwind sm breakpoint 640px)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handle = () => setIsMobile(mq.matches);
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  // Fetch existing serial numbers when modal opens and it's a debit note (and when page/size changes)
  // On mobile we use mobilePage for lazy load (append); on desktop we use currentPage (replace).
  // On mobile, once we've received a short batch (no more data), don't fetch again for that session.
  React.useEffect(() => {
    if (isOpen && isDebitNote && productCode && token) {
      if (isMobile && mobilePage > 0 && mobileHasNoMoreRef.current) {
        return; // already loaded all cards – don't refetch and show from start again
      }
      const page = isMobile ? mobilePage : currentPage;
      const size = isMobile ? MOBILE_PAGE_SIZE : pageSize;
      const append = isMobile && mobilePage > 0;
      fetchExistingSerialNumbers(page, size, append);
    } else if (!isOpen) {
      setExistingSerialNumbers([]);
      setTotalCount(null);
    }
  }, [isOpen, isDebitNote, productCode, token, locationId, currentPage, pageSize, searchQuery, isMobile, mobilePage]);

  // Function to fetch existing serial numbers
  // pageOverride/pageSizeOverride: used for mobile lazy load; append: when true, append to existing list
  const fetchExistingSerialNumbers = async (
    pageOverride?: number,
    pageSizeOverride?: number,
    append?: boolean
  ) => {
    if (!productCode || !token) return;

    const page = pageOverride ?? currentPage;
    const size = pageSizeOverride ?? pageSize;
    setIsLoadingExisting(true);
    try {
      console.log('\n🔍 ═══════════════════════════════════════════════════════════');
      console.log('🔍 FETCHING EXISTING SERIAL NUMBERS');
      console.log('🔍 ═══════════════════════════════════════════════════════════');
      const pageStart = page * size;
      const params = new URLSearchParams({
        productId: String(productCode),
        pageStart: String(pageStart),
        maxRecords: String(size),
        status: '1',
      });
      if (locationId != null) {
        params.set('locationId', String(locationId));
      }
      if (searchQuery.trim()) {
        params.set('serialNumber', searchQuery.trim());
      }
      const getSerailUrl = `${baseUrl}/api/GetSerail?${params.toString()}`;

      console.log('📋 Parameters:');
      console.log('   ├─ Product ID:', productCode);
      console.log('   ├─ Page Start:', pageStart);
      console.log('   ├─ Max Records:', size);
      console.log('   ├─ locationId:', locationId);
      if (searchQuery.trim()) console.log('   ├─ serialNumber:', searchQuery.trim());
      console.log('   ├─ status: 1');
      console.log('   └─ Base URL:', baseUrl);
      console.log('🌐 API URL:', getSerailUrl);
      console.log('🔍 ═══════════════════════════════════════════════════════════\n');

      const response = await fetch(getSerailUrl, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });

      console.log('\n📥 ═══════════════════════════════════════════════════════════');
      console.log('📥 EXISTING SERIAL NUMBERS API RESPONSE');
      console.log('📥 ═══════════════════════════════════════════════════════════');
      console.log('📊 Response Status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Error Response Body:', errorText);
        console.log('📥 ═══════════════════════════════════════════════════════════\n');
        throw new Error(`Failed to fetch existing serial numbers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Success Response Data:', JSON.stringify(data, null, 2));
      console.log('📥 ═══════════════════════════════════════════════════════════\n');

      // Transform API response to SerialNumber format (use pageStart in id for uniqueness when appending)
      if (data.data && Array.isArray(data.data)) {
        const existingSerials: SerialNumber[] = data.data.map((item: any, index: number) => {
          const rawCost = item.CostPrice ?? item.costprice ?? item.Costprice ?? item.UnitCost ?? item.OriginalCost;
          const costPrice = typeof rawCost === 'number' ? rawCost : (parseFloat(rawCost) || 0);
          return {
          id: `existing-${item.SerialNumber}-${pageStart}-${index}`,
          partNo: item.SerialNumber || '',
          spIncGST: item.Rate || 0,
          costPrice,
          batchNo: item.BatchNo || '',
          mfgDate: item.MfgDate || new Date().toISOString().split('T')[0],
          expiryDays: item.ExpiryDays?.toString() || '999999',
          auto: false,
          forever: item.ExpiryDays === 999999 || item.ExpiryDays === '999999',
          status: item.status || 1,
        };
        });
        if (append) {
          setExistingSerialNumbers((prev) => [...prev, ...existingSerials]);
          // End of list: this batch is shorter than requested - don't load more on mobile
          if (existingSerials.length < size) {
            mobileHasNoMoreRef.current = true;
          }
        } else {
          setExistingSerialNumbers(existingSerials);
          mobileHasNoMoreRef.current = false;
        }
        // Support optional totalCount from API (same as inventory Add Serial modal)
        if (!append) {
          if (typeof data.totalCount === 'number') {
            setTotalCount(data.totalCount);
          } else if (typeof data.total === 'number') {
            setTotalCount(data.total);
          } else {
            setTotalCount(null);
          }
        }
        console.log(`📦 Loaded ${existingSerials.length} existing serial numbers (page ${page + 1}${append ? ' append' : ''})`);
      }
    } catch (error) {
      console.error('\n💥 ═══════════════════════════════════════════════════════════');
      console.log('💥 FETCH EXISTING SERIAL NUMBERS ERROR');
      console.log('💥 ═══════════════════════════════════════════════════════════');
      console.error('💥 Error Details:', error);
      console.log('💥 ═══════════════════════════════════════════════════════════\n');
      
      toast({
        variant: 'destructive',
        title: 'Failed to Load Existing Serial Numbers',
        description: 'Could not load existing serial numbers. Please try again.',
      });
    } finally {
      setIsLoadingExisting(false);
    }
  };

  const baseUrl = getBaseUrl();
  const authToken = token?.startsWith('Bearer ') ? token : `Bearer ${token || ''}`;

  const handleAddSerial = () => {
    if (currentSerial.partNo.trim()) {
      const newSerial: SerialNumber = {
        ...currentSerial,
        id: `serial-${Date.now()}-${Math.random()}`,
      };
      setSerialNumbers([...serialNumbers, newSerial]);
      setCurrentSerial({
        id: '',
        partNo: '',
        spIncGST: productRate || 0,
        costPrice: 0,
        batchNo: '0',
        mfgDate: format(new Date(), 'yyyy-MM-dd'),
        expiryDays: defaultExpiryForever ? '999999' : '0',
        auto: false,
        forever: defaultExpiryForever,
      });
      
      // Show success message
      toast({
        title: 'Success',
        description: 'Serial number added successfully',
      });
    }
  };

  const handleClearAll = () => {
    setSerialNumbers([]);
  };

  const handleGenerate = () => {
    const newSerials: SerialNumber[] = [];
    for (let i = 0; i < generateCount; i++) {
      newSerials.push({
        ...currentSerial,
        id: `serial-${Date.now()}-${i}`,
        partNo: currentSerial.partNo ? `${currentSerial.partNo}-${i + 1}` : `SN-${Date.now()}-${i + 1}`,
      });
    }
    setSerialNumbers([...serialNumbers, ...newSerials]);
  };

  const handleAddToList = () => {
    if (isDebitNote) {
      // In debit note mode, add only selected existing serial numbers
      const selectedSerials = existingSerialNumbers.filter(serial => selectedSerialNumbers.has(serial.id));
      if (selectedSerials.length > 0) {
        onAddToList(selectedSerials);
        setSelectedSerialNumbers(new Set()); // Clear selection
        onClose();
      } else {
        toast({
          variant: 'destructive',
          title: 'No Selection',
          description: 'Please select at least one serial number to add.',
        });
      }
    } else {
      // In regular mode, add all newly created serial numbers
      if (serialNumbers.length > 0) {
        onAddToList(serialNumbers);
        setSerialNumbers([]);
        onClose();
      }
    }
  };

  const handleOpenAddForm = () => setShowAddForm(true);
  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setSerialNumberError('');
    setCurrentSerial({
      id: '',
      partNo: '',
      spIncGST: productRate || 0,
      costPrice: 0,
      batchNo: '0',
      mfgDate: format(new Date(), 'yyyy-MM-dd'),
      expiryDays: defaultExpiryForever ? '999999' : '0',
      auto: false,
      forever: defaultExpiryForever,
    });
  };

  const handleAddSerialSubmit = async () => {
    if (currentSerial.auto) {
      // Auto-generate serial numbers using API
      if (!productCode || !token) {
        setSerialNumberError('Product code or token not available');
        return;
      }

      setIsAddingSerial(true);
      setSerialNumberError('');

      try {
        console.log('\n🔢 ═══════════════════════════════════════════════════════════');
        console.log('🔢 GENERATE SERIAL NUMBERS API REQUEST');
        console.log('🔢 ═══════════════════════════════════════════════════════════');
        console.log('📋 Request Parameters:');
        console.log('   ├─ Product Code:', productCode);
        console.log('   ├─ Count:', generateCount);
        console.log('   └─ Base URL:', baseUrl);
        console.log('🌐 API URL:', `${baseUrl}/api/GenerateSerialNumbers`);
        console.log('📤 Request Body:', JSON.stringify({
          ProductCode: productCode,
          Count: generateCount
        }, null, 2));
        console.log('🔢 ═══════════════════════════════════════════════════════════\n');

        const response = await fetch(`${baseUrl}/api/GenerateSerialNumbers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify({
            ProductCode: productCode,
            Count: generateCount
          }),
        });

        console.log('\n📥 ═══════════════════════════════════════════════════════════');
        console.log('📥 GENERATE SERIAL NUMBERS API RESPONSE');
        console.log('📥 ═══════════════════════════════════════════════════════════');
        console.log('📊 Response Status:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.log('❌ Error Response Body:', errorText);
          console.log('📥 ═══════════════════════════════════════════════════════════\n');
          throw new Error(`Failed to generate serial numbers: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('✅ Success Response Data:', JSON.stringify(data, null, 2));
        console.log('📥 ═══════════════════════════════════════════════════════════\n');

        // Handle response based on status
        if (data.status === 0) {
          console.log('✅ Serial numbers generated successfully');
          console.log('📊 Generated Count:', data.data?.length || 0);
          
          // Transform API response to SerialNumber format
          const newSerials: SerialNumber[] = data.data.map((item: any, index: number) => ({
            id: `serial-${Date.now()}-${index}`,
            partNo: item.SerialNumber,
            spIncGST: currentSerial.spIncGST,
            costPrice: currentSerial.costPrice ?? 0,
            batchNo: currentSerial.batchNo,
            mfgDate: currentSerial.mfgDate,
            expiryDays: currentSerial.forever ? '999999' : currentSerial.expiryDays,
            auto: true,
            forever: currentSerial.forever,
          }));

          setSerialNumbers([...serialNumbers, ...newSerials]);
          
          // Reset form for next entry but keep the form open
          setCurrentSerial({
            id: '',
            partNo: '',
            spIncGST: productRate || 0,
            costPrice: 0,
            batchNo: '',
            mfgDate: format(new Date(), 'yyyy-MM-dd'),
            expiryDays: defaultExpiryForever ? '999999' : '0',
            auto: false,
            forever: defaultExpiryForever,
          });
          setIsAddingSerial(false);
          setSerialNumberError('');
          
          // Show success message
          toast({
            title: 'Success',
            description: `${newSerials.length} serial number(s) added successfully`,
          });
        } else if (data.status === -1) {
          console.log('❌ Error generating serial numbers');
          setSerialNumberError(data.message || 'Failed to generate serial numbers');
          toast({
            variant: 'destructive',
            title: 'Generation Failed',
            description: data.message || 'Failed to generate serial numbers',
          });
          setIsAddingSerial(false);
          return;
        } else {
          console.log('⚠️ Unexpected status:', data.status);
          setSerialNumberError('Unexpected error occurred while generating serial numbers');
          toast({
            variant: 'destructive',
            title: 'Unexpected Error',
            description: 'Unexpected error occurred while generating serial numbers',
          });
          setIsAddingSerial(false);
          return;
        }
      } catch (error) {
        console.error('\n💥 ═══════════════════════════════════════════════════════════');
        console.log('💥 GENERATE SERIAL NUMBERS API ERROR');
        console.log('💥 ═══════════════════════════════════════════════════════════');
        console.error('💥 Error Details:', error);
        console.log('💥 ═══════════════════════════════════════════════════════════\n');
        setSerialNumberError('Failed to generate serial numbers. Please try again.');
        toast({
          variant: 'destructive',
          title: 'API Error',
          description: 'Failed to generate serial numbers. Please try again.',
        });
        setIsAddingSerial(false);
      }
    } else {
      // Manual single serial number addition with API check
      if (!currentSerial.partNo.trim()) {
        setSerialNumberError('Part No is required');
        return;
      }

      if (!productCode || !token) {
        setSerialNumberError('Product code or token not available');
        return;
      }

      setIsAddingSerial(true);
      setSerialNumberError('');

      try {
        // First check if serial number exists
        console.log('=== CHECK SERIAL EXISTS API REQUEST (SUPPLIER INVOICE) ===');
        console.log('URL:', `${baseUrl}/api/CheckSerialExists`);
        console.log('Method:', 'POST');
        console.log('Headers:', {
          'Content-Type': 'application/json',
          'Authorization': authToken
        });
        console.log('Request Payload:', JSON.stringify({
          productId: productCode,
          serialNumber: currentSerial.partNo.trim()
        }, null, 2));

        // Add debug info
        console.log('DEBUG: Product Code:', productCode);
        console.log('DEBUG: Serial Number:', currentSerial.partNo.trim());
        console.log('DEBUG: Base URL:', baseUrl);
        console.log('DEBUG: Auth Token:', authToken.substring(0, 20) + '...');

        const checkResponse = await fetch(`${baseUrl}/api/CheckSerialExists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify({
            productId: productCode,
            serialNumber: currentSerial.partNo.trim()
          }),
        });

        console.log('DEBUG: Check Response Status:', checkResponse.status);
        console.log('DEBUG: Check Response OK:', checkResponse.ok);

        const checkData = await checkResponse.json();
        console.log('=== CHECK SERIAL EXISTS API RESPONSE (SUPPLIER INVOICE) ===');
        console.log('Status:', checkResponse.status);
        console.log('Response Data:', JSON.stringify(checkData, null, 2));

        // Handle response based on status
        console.log('DEBUG: Handling response with status:', checkData.status);
        
        if (checkData.status === 0) {
          console.log('DEBUG: Status is 0 - Serial number available, proceeding to add');
          // Serial number is available, add to the list
          const newSerial: SerialNumber = {
            ...currentSerial,
            id: `serial-${Date.now()}-${Math.random()}`,
          };
          setSerialNumbers([...serialNumbers, newSerial]);
          
          // Reset form for next entry but keep the form open
          setCurrentSerial({
            id: '',
            partNo: '',
            spIncGST: productRate || 0,
            costPrice: 0,
            batchNo: '0',
            mfgDate: format(new Date(), 'yyyy-MM-dd'),
            expiryDays: defaultExpiryForever ? '999999' : '0',
            auto: false,
            forever: defaultExpiryForever,
          });
          setIsAddingSerial(false);
          setSerialNumberError('');
          
          // Show success message
          toast({
            title: 'Success',
            description: 'Serial number added successfully',
          });
        } else if (checkData.status === -1 || checkData.status === 1) {
          console.log('DEBUG: Status is -1 or 1 - Serial number exists or error, showing error');
          // Serial number exists or error, show message and don't add
          setSerialNumberError(checkData.message || 'Serial number already exists or error occurred');
          setIsAddingSerial(false);
          return;
        } else {
          console.log('DEBUG: Unexpected status:', checkData.status);
          // Unexpected status
          setSerialNumberError('Unexpected error occurred while checking serial number');
          setIsAddingSerial(false);
          return;
        }
      } catch (error) {
        console.error('Error checking serial number:', error);
        setSerialNumberError('Failed to check serial number. Please try again.');
        setIsAddingSerial(false);
      }
    }
  };

  const handleDeleteSerial = (id: string) => {
    // Check if it's an existing serial number (starts with 'existing-')
    if (id.startsWith('existing-')) {
      setExistingSerialNumbers(existingSerialNumbers.filter(serial => serial.id !== id));
      // Also remove from selection if it was selected
      setSelectedSerialNumbers(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } else {
      setSerialNumbers(serialNumbers.filter(serial => serial.id !== id));
    }
  };

  // Handle checkbox selection for existing serial numbers
  const handleCheckboxChange = (id: string, checked: boolean) => {
    setSelectedSerialNumbers(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  // Handle select all checkbox
  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      const allIds = existingSerialNumbers.map(serial => serial.id);
      setSelectedSerialNumbers(new Set(allIds));
    } else {
      setSelectedSerialNumbers(new Set());
    }
  };

  const filteredSerialNumbers = [...existingSerialNumbers, ...serialNumbers].filter(serial =>
    serial.partNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
    serial.batchNo.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Mobile: load more when scrolling near bottom (lazy load). Stop once all cards are fetched.
  const handleCardsScroll = () => {
    if (!isMobile || !isDebitNote || isLoadingExisting) return;
    if (mobileHasNoMoreRef.current) return; // already loaded all – don't request more
    const el = cardsScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const nearBottom = scrollTop + clientHeight >= scrollHeight - 100;
    const hasMore =
      totalCount == null ||
      existingSerialNumbers.length < totalCount;
    if (nearBottom && hasMore) {
      setMobilePage((p) => p + 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 w-[95vw] sm:w-full flex flex-col overflow-hidden">
        {/* Header - fixed */}
        <div className="flex items-center justify-center p-4 border-b bg-gray-50 shrink-0">
          <DialogTitle className="text-xl font-semibold">
            Serial Number Entry - {productName} ({barcode})
          </DialogTitle>
        </div>

        {/* Header Actions - Search, Product Info, and Add Button - fixed */}
        <div className="px-4 py-4 border-b bg-gray-50 shrink-0">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
            {/* Search Box and selected count */}
            <div className="flex items-center gap-3 flex-1 max-w-md">
              <div className="relative flex-1 min-w-0">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search serial numbers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                />
              </div>
              {isDebitNote && (
                <span className="text-sm font-medium text-gray-600 whitespace-nowrap shrink-0">
                  {selectedSerialNumbers.size > 0 ? (
                    <span className="text-blue-600">
                      {selectedSerialNumbers.size} selected
                    </span>
                  ) : (
                    <span className="text-gray-400">0 selected</span>
                  )}
                </span>
              )}
            </div>
            
            {/* Product Info */}
            {!isDebitNote && (
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="font-medium text-gray-700">
                  {productName}
                </div>
                <div className="text-xs text-gray-500">
                  Barcode: {barcode}
                </div>
                {productRate && (
                  <div className="text-xs text-green-600 font-medium">
                    Rate: ₹{productRate.toFixed(2)}
                  </div>
                )}
              </div>
            )}
            {isDebitNote && (
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="font-medium text-gray-700">
                  {productName}
                </div>
                {/* Lazy load count: so you can see cards loaded (e.g. 20 → 40 as you scroll) */}
                {isDebitNote && isMobile && (
                  <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
                    Showing {existingSerialNumbers.length}
                    {totalCount != null ? ` of ${totalCount}` : ''} serials
                  </span>
                )}
              </div>
            )}
            
            {/* Add Button */}
            {!isDebitNote && (
              <Button
                onClick={handleOpenAddForm}
                className="bg-teal-500 hover:bg-teal-600 text-white w-full sm:w-auto px-6 py-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Serial
              </Button>
            )}
          </div>
        </div>

        {/* Add Serial Number Form - fixed when open */}
        {!isDebitNote && showAddForm && (
          <div className="px-4 py-4 border-b bg-blue-50 shrink-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-900">Add New Serial Number</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseAddForm}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Error Message */}
            {serialNumberError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                <p className="text-sm text-red-700">{serialNumberError}</p>
              </div>
            )}

            <div className="px-4 lg:px-0">
              <div className="flex flex-col lg:flex-row gap-4 pb-2">
                <div className="space-y-2 w-full lg:min-w-[200px]">
                  <Label htmlFor="partNo" className="text-sm font-medium text-blue-900">
                    Part No / Serial Number *
                  </Label>
                  <Input
                    id="partNo"
                    placeholder={currentSerial.auto ? "Auto-generated serial numbers" : "Enter Part No"}
                    value={currentSerial.partNo}
                    onChange={(e) => {
                      setCurrentSerial({ ...currentSerial, partNo: e.target.value });
                      setSerialNumberError(''); // Clear error on input
                    }}
                    className={`w-full ${serialNumberError && !currentSerial.partNo?.trim() ? 'border-red-300 focus:border-red-500' : ''}`}
                    disabled={isAddingSerial || currentSerial.auto}
                    readOnly={currentSerial.auto}
                  />
                  {currentSerial.auto && (
                    <p className="text-xs text-blue-600">Serial numbers will be auto-generated</p>
                  )}
                  
                  {/* Auto and Count moved below Part No */}
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center space-x-1">
                      <Checkbox
                        id="auto"
                        checked={currentSerial.auto}
                        onCheckedChange={(checked) => {
                          const newAuto = !!checked;
                          setCurrentSerial({ 
                            ...currentSerial, 
                            auto: newAuto,
                            partNo: newAuto ? '' : currentSerial.partNo // Clear partNo when auto is enabled
                          });
                        }}
                        disabled={isAddingSerial}
                        className="scale-75"
                      />
                      <Label htmlFor="auto" className="text-xs text-blue-700">Auto</Label>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Label htmlFor="generateCount" className="text-xs text-blue-700">
                        Count:
                      </Label>
                      <Input
                        id="generateCount"
                        type="number"
                        min="1"
                        value={generateCount}
                        onChange={(e) => setGenerateCount(parseInt(e.target.value) || 1)}
                        className="w-12 h-6 text-xs px-1"
                        disabled={isAddingSerial}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 w-full lg:min-w-[150px]">
                  <Label htmlFor="spIncGST" className="text-sm font-medium text-blue-900">
                    SP Inc GST {productRate && <span className="text-xs text-green-600">(Product Rate)</span>}
                  </Label>
                  <Input
                    id="spIncGST"
                    type="number"
                    step="0.01"
                    placeholder={productRate ? `Product Rate: ${productRate.toFixed(2)}` : "0.00"}
                    value={currentSerial.spIncGST}
                    onChange={(e) => setCurrentSerial({ ...currentSerial, spIncGST: parseFloat(e.target.value) || 0 })}
                    className={`w-full ${productRate && currentSerial.spIncGST === productRate ? 'border-green-300 bg-green-50' : ''}`}
                    disabled={isAddingSerial}
                  />
                  {productRate && (
                    <p className="text-xs text-green-600">Product rate: ₹{productRate.toFixed(2)}</p>
                  )}
                </div>

                {!hideCostPrice && (
                <div className="space-y-2 w-full lg:min-w-[120px]">
                  <Label htmlFor="costPrice" className="text-sm font-medium text-blue-900">
                    Costprice
                  </Label>
                  <Input
                    id="costPrice"
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={currentSerial.costPrice ?? 0}
                    onChange={(e) => setCurrentSerial({ ...currentSerial, costPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full"
                    disabled={isAddingSerial}
                  />
                </div>
                )}

                <div className="space-y-2 w-full lg:min-w-[100px]">
                  <Label htmlFor="batchNo" className="text-sm font-medium text-blue-900">
                    Batch No
                  </Label>
                  <Input
                    id="batchNo"
                    placeholder="Enter Batch No"
                    value={currentSerial.batchNo}
                    onChange={(e) => setCurrentSerial({ ...currentSerial, batchNo: e.target.value })}
                    className="w-full"
                    disabled={isAddingSerial}
                  />
                </div>

                <div className="space-y-2 w-full lg:min-w-[140px]">
                  <Label htmlFor="mfgDate" className="text-sm font-medium text-blue-900">
                    MFG Date
                  </Label>
                  <Input
                    id="mfgDate"
                    type="date"
                    value={currentSerial.mfgDate}
                    onChange={(e) => setCurrentSerial({ ...currentSerial, mfgDate: e.target.value })}
                    className="w-full"
                    disabled={isAddingSerial}
                  />
                </div>

                <div className="space-y-2 w-full lg:min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="expiryDays" className="text-sm font-medium text-blue-900">
                      Expiry Days
                    </Label>
                    <div className="flex items-center space-x-1">
                      <Checkbox
                        id="forever"
                        checked={currentSerial.forever}
                        onCheckedChange={(checked) => {
                          const newForever = !!checked;
                          setCurrentSerial({ 
                            ...currentSerial, 
                            forever: newForever,
                            expiryDays: newForever ? '999999' : '0' // Set 999999 when forever is checked, otherwise 0
                          });
                        }}
                        disabled={isAddingSerial}
                      />
                      <Label htmlFor="forever" className="text-xs text-blue-700 cursor-pointer">Forever</Label>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id="expiryDays"
                      placeholder="Enter Expiry Days"
                      value={currentSerial.forever ? '999999' : currentSerial.expiryDays}
                      onChange={(e) => setCurrentSerial({ ...currentSerial, expiryDays: e.target.value })}
                      className="flex-1"
                      disabled={isAddingSerial || currentSerial.forever}
                      readOnly={currentSerial.forever}
                    />
                    <div className="hidden lg:flex items-center gap-1">
                      <Button
                        variant="outline"
                        onClick={handleCloseAddForm}
                        disabled={isAddingSerial}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50 h-10 w-10 p-0 flex-shrink-0"
                        title="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={handleAddSerialSubmit}
                        disabled={isAddingSerial || (!currentSerial.partNo.trim() && !currentSerial.auto)}
                        className="bg-blue-600 hover:bg-blue-700 text-white h-10 w-10 p-0 flex-shrink-0"
                        title={currentSerial.auto ? `Generate ${generateCount} Serial(s)` : 'Add Serial'}
                      >
                        {isAddingSerial ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Buttons - Side by Side */}
            <div className="lg:hidden px-4 mt-6">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleCloseAddForm}
                  disabled={isAddingSerial}
                  className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 h-12"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleAddSerialSubmit}
                  disabled={isAddingSerial || (!currentSerial.partNo.trim() && !currentSerial.auto)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-12"
                >
                  {isAddingSerial ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {currentSerial.auto ? 'Generating...' : 'Adding...'}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      {currentSerial.auto ? `Generate ${generateCount} Serial(s)` : 'Add Serial'}
                    </>
                  )}
                </Button>
              </div>
            </div>

        </div>)}

        {/* Table Section - scrollable on mobile; flex-1 min-h-0 so only this area scrolls */}
        <div
          ref={cardsScrollRef}
          onScroll={isMobile && isDebitNote ? handleCardsScroll : undefined}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-auto px-2 sm:px-4 py-4"
        >
          {isLoadingExisting ? (
            <div className="text-center py-8 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading existing serial numbers...
            </div>
          ) : filteredSerialNumbers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchQuery ? 'No serial numbers found matching your search' : 
               isDebitNote ? 'No existing serial numbers found for this product' : 'No serial numbers added yet'}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden sm:block border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isDebitNote && (
                          <TableHead className="min-w-[50px]">
                            <Checkbox
                              checked={existingSerialNumbers.length > 0 && existingSerialNumbers.every(serial => selectedSerialNumbers.has(serial.id))}
                              onCheckedChange={handleSelectAllChange}
                              className="scale-75"
                            />
                          </TableHead>
                        )}
                        <TableHead className="min-w-[120px]">Part No</TableHead>
                        <TableHead className="min-w-[100px]">SP Inc GST</TableHead>
                        {!hideCostPrice && <TableHead className="min-w-[100px]">Costprice</TableHead>}
                        <TableHead className="min-w-[100px]">Batch No</TableHead>
                        <TableHead className="min-w-[100px]">MFG Date</TableHead>
                        <TableHead className="min-w-[100px]">Expiry Days</TableHead>
                        <TableHead className="min-w-[80px]">Auto</TableHead>
                        <TableHead className="min-w-[80px]">Forever</TableHead>
                        <TableHead className="min-w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSerialNumbers.map((serial) => {
                        const isExisting = serial.id.startsWith('existing-');
                        return (
                        <TableRow key={serial.id} className={`hover:bg-gray-50 ${isExisting ? 'bg-blue-50' : ''}`}>
                          {isDebitNote && isExisting && (
                            <TableCell>
                              <Checkbox
                                checked={selectedSerialNumbers.has(serial.id)}
                                onCheckedChange={(checked) => handleCheckboxChange(serial.id, !!checked)}
                                className="scale-75"
                              />
                            </TableCell>
                          )}
                          {!isDebitNote && isExisting && (
                            <TableCell></TableCell>
                          )}
                          <TableCell className="font-medium">
                            {serial.partNo}
                            {isExisting && (
                              <span className="ml-2 text-xs bg-blue-100 px-2 py-1 rounded text-blue-700">Existing</span>
                            )}
                          </TableCell>
                          <TableCell>₹{serial.spIncGST.toFixed(2)}</TableCell>
                          {!hideCostPrice && <TableCell>₹{(serial.costPrice ?? 0).toFixed(2)}</TableCell>}
                          <TableCell>{serial.batchNo || 'N/A'}</TableCell>
                          <TableCell>{serial.mfgDate}</TableCell>
                          <TableCell>{serial.expiryDays || 'N/A'}</TableCell>
                          <TableCell>
                            {serial.auto && (
                              <span className="text-xs bg-blue-100 px-2 py-1 rounded">Auto</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {serial.forever && (
                              <span className="text-xs bg-green-100 px-2 py-1 rounded">Forever</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={() => handleDeleteSerial(serial.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 border-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden space-y-3">
                {filteredSerialNumbers.map((serial) => {
                  const isExisting = serial.id.startsWith('existing-');
                  return (
                  <div key={serial.id} className={`border rounded-lg p-4 shadow-sm ${isExisting ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-start gap-3 flex-1">
                        {isDebitNote && isExisting && (
                          <Checkbox
                            checked={selectedSerialNumbers.has(serial.id)}
                            onCheckedChange={(checked) => handleCheckboxChange(serial.id, !!checked)}
                            className="scale-75 mt-1"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-base text-gray-900 break-words">
                            {serial.partNo}
                            {isExisting && (
                              <span className="ml-2 text-xs bg-blue-100 px-2 py-1 rounded text-blue-700">Existing</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            ₹{serial.spIncGST.toFixed(2)}
                            {!hideCostPrice && serial.costPrice != null && serial.costPrice !== 0 && (
                              <span className="text-gray-500 ml-1">· Cost: ₹{(serial.costPrice ?? 0).toFixed(2)}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDeleteSerial(serial.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 border-red-300 flex-shrink-0 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {!hideCostPrice && (
                      <div>
                        <span className="font-medium text-gray-700">Cost price:</span>
                        <p className="text-gray-900">₹{(serial.costPrice ?? 0).toFixed(2)}</p>
                      </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-700">Batch:</span>
                        <p className="text-gray-900 break-words">{serial.batchNo || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">MFG:</span>
                        <p className="text-gray-900">{serial.mfgDate}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Expiry:</span>
                        <p className="text-gray-900">{serial.expiryDays || 'N/A'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {serial.auto && (
                          <span className="text-xs bg-blue-100 px-2 py-1 rounded">Auto</span>
                        )}
                        {serial.forever && (
                          <span className="text-xs bg-green-100 px-2 py-1 rounded">Forever</span>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Mobile: loading more indicator (lazy load) */}
              {isDebitNote && isMobile && isLoadingExisting && mobilePage > 0 && (
                <div className="sm:hidden flex items-center justify-center py-4 text-gray-500 text-sm">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading more...
                </div>
              )}

              {/* Pagination (debit note flow only; hidden on mobile - use lazy load there) */}
              {isDebitNote && !isLoadingExisting && (
                <div className="hidden sm:flex flex-wrap items-center justify-between gap-3 px-2 sm:px-4 py-3 border-t bg-gray-50/80 shrink-0">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">Rows per page</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        const newSize = Number(e.target.value);
                        setPageSize(newSize);
                        setCurrentPage(0);
                      }}
                      className="h-9 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-600">
                      {totalCount != null
                        ? `Page ${currentPage + 1} of ${Math.max(1, Math.ceil(totalCount / pageSize))}`
                        : `Page ${currentPage + 1}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="h-9 gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={
                        totalCount != null
                          ? (currentPage + 1) * pageSize >= totalCount
                          : existingSerialNumbers.length < pageSize
                      }
                      className="h-9 gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions - fixed */}
        <div className="flex justify-between p-4 border-t bg-gray-50 shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            onClick={handleAddToList} 
            disabled={isDebitNote ? selectedSerialNumbers.size === 0 : serialNumbers.length === 0}
            className="bg-teal-500 hover:bg-teal-600 text-white flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add to List ({isDebitNote ? selectedSerialNumbers.size : serialNumbers.length})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SerialNumberEntryModal;
